import type { ReactNode } from "react";
import { fa } from "../i18n/fa";
import { useUprightNeeded } from "../telegram/orientation";

/**
 * «Turn your phone upright» (card-f5 item 3) — shown over the whole app while a PHONE shows it
 * sideways: opened sideways (Telegram's lock freezes the CURRENT orientation, so the app locks only
 * once the phone is upright), or a Telegram older than 8.0, which cannot lock. Telegram Desktop and
 * the web clients never see it. See `telegram/orientation.ts`.
 *
 * Mounted ONCE in main.tsx BESIDE the app — never around it — so it only ever covers: a table under
 * it keeps its node, its socket and its state, and a turn re-renders this screen alone. Opaque and
 * above every app overlay (the maintenance warning, 90, is the highest), so nothing on the squashed
 * table can be tapped through it. The splash's felt (Boot) in both themes; padded off all four
 * safe-area insets, because sideways the notch and the nav bar sit at the SIDES.
 */
export function UprightScreen(): ReactNode {
  const needed = useUprightNeeded();
  if (!needed) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      dir="rtl"
      data-upright-screen=""
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        display: "grid",
        placeItems: "center",
        textAlign: "center",
        paddingTop: "max(env(safe-area-inset-top), var(--tg-safe-top, 0px))",
        paddingRight: "max(env(safe-area-inset-right), var(--tg-safe-right, 0px))",
        paddingBottom: "max(env(safe-area-inset-bottom), var(--tg-safe-bottom, 0px))",
        paddingLeft: "max(env(safe-area-inset-left), var(--tg-safe-left, 0px))",
        background:
          "radial-gradient(130% 120% at 50% 30%, var(--felt-2) 0%, var(--felt-edge) 55%, var(--boot-vignette) 100%)",
        color: "var(--text)",
      }}
    >
      <div style={{ display: "grid", gap: 10, justifyItems: "center", padding: 22, maxWidth: 340 }}>
        <div aria-hidden style={{ fontSize: 40 }}>
          📱
        </div>
        <p style={{ margin: 0, fontWeight: 800, fontSize: 17 }}>{fa.upright.title}</p>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          {fa.upright.body}
        </p>
      </div>
    </div>
  );
}
